/**
 * Client state for a host-EHR integration that drives the app from outside it
 * (Medical Director today): whether one is attached, what it lets us do, the
 * callbacks it hands us, and which patient it currently has open.
 *
 * One atom per field, not one atom for the group. Every reader here takes a
 * single field — the paywall reads `hasIntegration`, the banner reads
 * `integrationOptions` — and a single atom for all four would re-render the
 * twelve `hasIntegration` readers every time the medical director sidebar
 * reports a patient change.
 *
 * `integrationAtom` composes the four for the handful of readers that want the
 * whole shape. It is derived, and therefore read-only, so it cannot become a
 * write path back to a single blob.
 */
import { logger } from '@heidi/runtime/logger';
import { type EHRProvider } from '@heidi/schema/ml-scribe';
import { atom, getDefaultStore } from 'jotai';

export interface IntegrationOptions {
  ehrName?: string;
  ehrBaseUrl?: string;
  banner?: boolean;
  pushNotes?: boolean;
  patientDemographics?: boolean;
}

export interface IntegrationCallbacks {
  onPushNotes?: (notes: string) => void;
  onSwitchPatient?: (newPatientId: string) => void;
  onCheckCurrentPatient?: () => string | undefined;
  onLogout?: () => void;
}

export interface IntegrationData {
  patientId?: string;
  ehrProvider?: EHRProvider;
}

export type IntegrationState = {
  hasIntegration: boolean;
  integrationOptions: IntegrationOptions;
  integrationCallbacks: IntegrationCallbacks;
  integrationData: IntegrationData;
};

export const hasIntegrationAtom = atom<boolean>(false);
export const integrationOptionsAtom = atom<IntegrationOptions>({});
export const integrationCallbacksAtom = atom<IntegrationCallbacks>({});
export const integrationDataAtom = atom<IntegrationData>({});

/** The whole shape, for the readers that destructure more than one field. */
export const integrationAtom = atom<IntegrationState>((get) => ({
  hasIntegration: get(hasIntegrationAtom),
  integrationOptions: get(integrationOptionsAtom),
  integrationCallbacks: get(integrationCallbacksAtom),
  integrationData: get(integrationDataAtom),
}));

export function setHasIntegration(hasIntegration: boolean): void {
  getDefaultStore().set(hasIntegrationAtom, hasIntegration);
}

/** MERGES over the current options; an absent key keeps its current value. */
export function setIntegrationOptions(integrationOptions: IntegrationOptions): void {
  const store = getDefaultStore();
  store.set(integrationOptionsAtom, {
    ...store.get(integrationOptionsAtom),
    ...integrationOptions,
  });
}

/** MERGES over the current callbacks; an absent key keeps its current value. */
export function setIntegrationCallbacks(
  integrationCallbacks: IntegrationCallbacks
): void {
  const store = getDefaultStore();
  store.set(integrationCallbacksAtom, {
    ...store.get(integrationCallbacksAtom),
    ...integrationCallbacks,
  });
}

/**
 * MERGES over the current data. An explicit `patientId: undefined` therefore
 * clears the patient while leaving `ehrProvider` in place — which is how the
 * medical director event handler reacts to a clinical window closing with no
 * patient open.
 */
export function setIntegrationData(integrationData: IntegrationData): void {
  logger.debug('updating...', integrationData);
  const store = getDefaultStore();
  store.set(integrationDataAtom, {
    ...store.get(integrationDataAtom),
    ...integrationData,
  });
}

/** Every field back to its initial value, unconditionally. */
export function clearIntegration(): void {
  const store = getDefaultStore();
  store.set(hasIntegrationAtom, false);
  store.set(integrationOptionsAtom, {});
  store.set(integrationCallbacksAtom, {});
  store.set(integrationDataAtom, {});
}

/**
 * The provider the v1 analytics payload carries as `from_integration`. The two
 * legacy senders are plain functions with no React context to read from, so
 * they take the value through this getter rather than through a hook.
 */
export function getIntegrationEhrProvider(): EHRProvider | undefined {
  return getDefaultStore().get(integrationDataAtom).ehrProvider;
}
